import { createElement } from "react";

import {
  ConfigurationFormType,
  ConfigurationPageTables,
  ConfigurationPageType,
  ConfigurationTableFilterType,
} from "../interfaces";
import { FormatType } from "../../Alert/Filter/FilterType";
import { SuggestedBoundHint } from "../../Pricing/SuggestedBoundHint";

const SETUP_PRICE_PAGE_PRICES_TAB: ConfigurationPageTables = {
  description: "CONF_DESC_SETUP_PRICES",
  type: ConfigurationPageType.TABLE,
  label: "PRICES",
  path: "config-prices",
  model: {
    dynamicPath: true,
    entityName: "uh_cat.short_name",
    label: "PRICES",
    path: "/api/config-prices",
    name: "Prices",
    attributes: [
      {
        key: "uh_cat.property.hebergement_nom_court",
        order: 1,
        label: "PROPERTY",
        visible: true,
      },
      {
        align: "right",
        key: "uh_cat.short_name",
        order: 2,
        label: "UH_CAT",
        visible: true,
      },
      {
        align: "right",
        key: "occupation_date",
        order: 3,
        label: "OCCUPATION_DATE",
        type: FormatType.DATE,
        visible: true,
      },
      {
        align: "right",
        key: "price_after_taxes",
        order: 4,
        label: "CONF_PRICE_AFTER_TAXES",
        visible: true,
      },
      {
        align: "right",
        key: "price_before_taxes",
        order: 5,
        label: "CONF_PRICE_BEFORE_TAXES",
        visible: true,
      },
      {
        key: "grid_type.code",
        order: 6,
        label: "GRID_TYPE",
        visible: true,
      },
      {
        key: "owner.username",
        order: 7,
        label: "UPDATED_BY",
        visible: true,
      },
      {
        align: "right",
        key: "updated_at",
        order: 8,
        label: "UPDATED_AT",
        type: FormatType.DATE,
        visible: true,
      },
    ],
    filters: [
      {
        type: ConfigurationTableFilterType.Select,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        key: "property",
        labelKey: "hebergement_nom_court",
        label: "PROPERTY",
        lookup: "hebergement_uh_cat__property",
      },
      {
        type: ConfigurationTableFilterType.Select,
        api: "/api/uh-cats/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
        },
        key: "uh_cat",
        labelKey: "short_name",
        label: "UH_CAT",
        lookup: "hebergement_uh_cat",
        watchLookup: "hebergement_uh_cat__property",
      },
      {
        type: ConfigurationTableFilterType.DayOfWeek,
        lookup: "weekdays",
        label: "DAY_OF_WEEK",
      },
      {
        type: ConfigurationTableFilterType.DatePicker,
        lookup: "occupation_date__gte",
        label: "START_DATE",
        maxDateField: "occupation_date__lte",
      },
      {
        type: ConfigurationTableFilterType.DatePicker,
        lookup: "occupation_date__lte",
        label: "END_DATE",
        minDateField: "occupation_date__gte",
      },
      {
        type: ConfigurationTableFilterType.Search,
      },
      {
        type: ConfigurationTableFilterType.ResetButton,
      },
    ],
    export: { path: "/api/config-prices/export" },
    import: { path: "/api/config-prices/presign-url" },
  },
  options: {
    formFields: [
      {
        type: ConfigurationFormType.Select,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        key: "property",
        label: "PROPERTY",
        labelKey: "hebergement_nom_court",
        watchedBy: "uh_cat",
      },
      {
        type: ConfigurationFormType.Select,
        api: "/api/uh-cats/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
          property: {
            path: "uh_cat.property.id",
          },
        },
        key: "uh_cat",
        label: "UH_CAT",
        labelKey: "short_name",
        watchField: [
          {
            paramsKey: "property",
            paramsValue: "id",
            watchFieldPath: "property",
          },
        ],
      },
      {
        type: ConfigurationFormType.Select,
        api: "/api/grid-types/",
        apiParams: {
          page_size: 100,
          ordering: "code",
        },
        key: "grid_type",
        label: "GRID_TYPE",
        labelKey: "code",
      },
      {
        key: "occupation_date",
        label: "OCCUPATION_DATE",
        type: ConfigurationFormType.DatePicker,
      },
      {
        inputType: "number",
        key: "price_after_taxes",
        label: "CONF_PRICE_AFTER_TAXES",
        type: ConfigurationFormType.Input,
      },
      {
        inputType: "number",
        key: "price_before_taxes",
        label: "CONF_PRICE_BEFORE_TAXES",
        type: ConfigurationFormType.Input,
      },
    ],
    creatable: true,
    createOptions: [
      {
        key: "property",
        flex: "50%",
        required: false,
      },
      {
        key: "uh_cat",
        flex: "50%",
        required: true,
      },
      {
        key: "grid_type",
        flex: "100%",
        required: true,
      },
      {
        key: "occupation_date",
        flex: "100%",
        required: true,
      },
      {
        key: "price_after_taxes",
        flex: "50%",
        required: true,
      },
      {
        key: "price_before_taxes",
        flex: "50%",
        required: false,
      },
    ],
    copyable: false,
    editable: true,
    editOptions: [
      {
        key: "property",
        flex: "50%",
        readonly: true,
      },
      {
        key: "uh_cat",
        flex: "50%",
        readonly: true,
      },
      {
        key: "grid_type",
        flex: "100%",
        readonly: true,
      },
      {
        key: "occupation_date",
        flex: "50%",
        readonly: true,
      },
      {
        key: "price_after_taxes",
        flex: "50%",
        required: true,
      },
      {
        key: "price_before_taxes",
        flex: "50%",
        required: false,
      },
    ],
    deletable: true,
  },
};

const SETUP_PRICE_PAGE_BUY_UP_TAB: ConfigurationPageTables = {
  description: "CONF_PAGE_DESC_DEFAULT",
  type: ConfigurationPageType.TABLE,
  label: "BUY_UP_UNIT",
  path: "buy-ups",
  model: {
    dynamicPath: true,
    entityName: "uh_cat.short_name",
    label: "BUY_UP",
    path: "/api/v2/buy-ups",
    name: "buy up",
    attributes: [
      {
        key: "uh_cat.property.hebergement_nom_court",
        order: 1,
        label: "PROPERTY",
        visible: true,
      },
      {
        key: "uh_cat.short_name",
        order: 2,
        label: "UH_CAT",
        visible: true,
      },
      {
        key: "uh_cat_ref.short_name",
        order: 3,
        label: "UH_CAT_REF",
        visible: true,
      },
      {
        align: "right",
        key: "start_date",
        order: 4,
        label: "DATE_START",
        type: FormatType.DATE,
        visible: true,
      },
      {
        align: "right",
        key: "end_date",
        order: 5,
        label: "DATE_END",
        type: FormatType.DATE,
        visible: true,
      },
      {
        key: "weekdays",
        order: 5.5,
        label: "DAYS_OF_OCCUPATION",
        type: FormatType.WEEKDAYS_ROUNDED,
        visible: true,
      },
      {
        key: "user_input",
        order: 6,
        label: "CALCUL_EDITOR",
        visible: true,
        type: FormatType.CALCUL_EDITOR,
        editorInputs: [
          {
            value: "nb_pax",
            label: "NB_ADULT_PAX",
            isVariable: true,
          },
          {
            value: "ref_uhcat",
            label: "UH_CAT_REF",
            isVariable: true,
          },
        ],
      },
      {
        align: "right",
        key: "rounding_value_v2",
        order: 9,
        label: "ROUNDING_VALUE",
        visible: true,
      },
      {
        key: "rounding_direction_v2",
        order: 10,
        label: "ROUNDING_DIRECTION",
        visible: true,
        type: FormatType.TRANSLATION,
      },
      {
        key: "user_created_by.username",
        order: 11,
        label: "UPDATED_BY",
        visible: true,
      },
      {
        align: "right",
        key: "updated_at",
        order: 12,
        label: "UPDATED_AT",
        type: FormatType.DATE,
        visible: true,
      },
    ],
    filters: [
      {
        type: ConfigurationTableFilterType.Select,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        key: "property",
        labelKey: "hebergement_nom_court",
        label: "PROPERTY",
        lookup: "uh_cat__property",
      },
      {
        type: ConfigurationTableFilterType.Select,
        api: "/api/uh-cats/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
        },
        key: "uh_cat",
        labelKey: "short_name",
        label: "UH_CAT",
        lookup: "uh_cat",
        watchLookup: "uh_cat__property",
      },
      {
        type: ConfigurationTableFilterType.Search,
      },
      {
        type: ConfigurationTableFilterType.ResetButton,
      },
    ],
    export: { path: "/api/v2/buy-ups/export" },
    import: { path: "/api/v2/buy-ups/presign-url" },
  },
  options: {
    // Saves the rule and reprices its room from today on with it.
    extraSubmits: [
      { action: "apply_in_cascade", label: "BUY_UP_SAVE_AND_APPLY" },
    ],
    // A rule that cannot be applied says why.
    displayFormError: true,
    formFields: [
      {
        type: ConfigurationFormType.Select,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        key: "uh_cat.property",
        label: "PROPERTY",
        labelKey: "hebergement_nom_court",
        watchedBy: ["uh_cat", "uh_cat_ref"],
        watchLookup: "uh_cat__property",
      },
      {
        type: ConfigurationFormType.Select,
        api: "/api/uh-cats/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
          property: {
            path: "uh_cat.property.id",
          },
        },
        keepApiData: ["uh_type_nandc", "nb_adult_pax"],
        key: "uh_cat",
        label: "UH_CAT",
        labelKey: "short_name",
        watchField: [
          {
            paramsKey: "property",
            paramsValue: "id",
            watchFieldPath: "uh_cat.property",
          },
        ],
        watchedBy: "uh_cat_ref",
      },
      {
        type: ConfigurationFormType.AutoFilledInput,
        api: "/api/uh-cats/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
          property: {
            path: "uh_cat.property.id",
          },
          uh_type_nandc: {
            path: "uh_cat.uh_type_nandc.id",
          },
          is_uh_type_reference: true,
        },
        key: "uh_cat_ref",
        label: "UH_CAT_REF",
        labelKey: "short_name",
        watchField: [
          {
            paramsKey: "property",
            paramsValue: "id",
            watchFieldPath: "uh_cat.property",
          },
          {
            paramsKey: "uh_type_nandc",
            paramsValue: "uh_type_nandc.id",
            watchFieldPath: "uh_cat",
          },
        ],
      },
      {
        key: "separatorOccupation",
        label: "BUY_UP_SECTION_OCCUPATION",
        subLabel: "BUY_UP_SECTION_OCCUPATION_DESC",
        type: ConfigurationFormType.Separator,
      },
      {
        key: "date_range",
        startDateField: "start_date",
        endDateField: "end_date",
        startDateLabel: "DATE_START",
        endDateLabel: "DATE_END",
        type: ConfigurationFormType.DateRangePicker,
        defaultValue: null,
      },
      {
        key: "weekdays",
        label: "DAYS_OF_OCCUPATION",
        type: ConfigurationFormType.WeekDaysRounded,
      },
      {
        key: "separatorFormula",
        label: "BUY_UP_SECTION_FORMULA",
        subLabel: "BUY_UP_SECTION_FORMULA_DESC",
        type: ConfigurationFormType.Separator,
      },
      {
        key: "user_input",
        type: ConfigurationFormType.CalculEditor,
        tooltip: "CALCUL_EDITOR_TOOLTIP",
        label: "CALCUL_EDITOR",
        placeholder: "CALCUL_EDITOR_PLACEHOLDER_BUY_UP_PRICE_TAB",
        inputMaxLength: 128,
        editorInputs: [
          {
            key: "plus",
            label: "+",
            value: "+",
          },
          {
            key: "minus",
            label: "-",
            value: "-",
          },
          {
            key: "multiplication",
            label: "*",
            value: "*",
          },
          {
            key: "division",
            label: "/",
            value: "/",
          },
          {
            key: "nb_pax",
            type: "autofill",
            label: "NB_ADULT_PAX",
            tooltip: "NB_PAX_CALCULATOR_TOOLTIP",
            translate: true,
            value: "nb_pax",
            isVariable: true,
            warning: {
              label: "NB_PAX_CALCULATOR_WARNING",
              value: null,
              watchField: "nb_pax",
            },
            watchField: [
              {
                paramsKey: "nb_pax",
                paramsValue: "nb_adult_pax",
                watchFieldPath: "uh_cat",
              },
            ],
          },
          {
            key: "grid",
            label: "UH_CAT_REF",
            translate: true,
            value: "ref_uhcat",
            isVariable: true,
            required: true,
          },
        ],
      },
      {
        inputType: "number",
        key: "rounding_value_v2",
        label: "ROUNDING_VALUE",
        type: ConfigurationFormType.Input,
      },
      {
        key: "rounding_direction_v2",
        label: "ROUNDING_DIRECTION",
        type: ConfigurationFormType.StaticSelect,
      },
      {
        key: "user_input_simulation",
        type: ConfigurationFormType.CalculEditorSimulation,
        tooltip: "CALCUL_EDITOR_TOOLTIP_SIMULATION",
        label: "CALCUL_EDITOR_SIMULATION",
        api: "/api/v2/buy-ups/apply/",
        watchField: "user_input",
        editorInputs: [
          {
            type: "autofill",
            key: "nb_pax",
            label: "NB_ADULT_PAX",
            value: "nb_pax",
            isVariable: true,
            tooltip: "NB_PAX_SIMULATION_TOOLTIP",
            defaultValue: 1,
            watchField: [
              {
                paramsKey: "nb_pax",
                paramsValue: "nb_adult_pax",
                watchFieldPath: "uh_cat",
              },
            ],
          },
          {
            key: "ref_uhcat",
            label: "UH_CAT_REF_PRICE",
            value: "ref_uhcat",
            placeholder: "100",
            isVariable: true,
          },
        ],
      },
    ],
    creatable: true,
    createOptions: [
      {
        key: "uh_cat.property",
        flex: "33%",
        required: true,
      },
      {
        key: "uh_cat",
        flex: "33%",
        required: true,
      },
      {
        key: "nb_pax",
        flex: "100%",
        // visible: false,
      },
      {
        key: "uh_cat_ref",
        flex: "33%",
        required: true,
      },
      {
        key: "separatorOccupation",
        flex: "100%",
      },
      {
        key: "date_range",
        flex: "100%",
      },
      {
        key: "weekdays",
        flex: "100%",
        required: true,
      },
      {
        key: "separatorFormula",
        flex: "100%",
      },
      {
        key: "user_input",
        flex: "100%",
        required: true,
      },
      {
        key: "rounding_value_v2",
        flex: "50%",
      },
      {
        key: "rounding_direction_v2",
        flex: "50%",
      },
      {
        key: "user_input_simulation",
        flex: "100%",
      },
    ],
    copyable: false,
    editable: true,
    editOptions: [
      {
        key: "uh_cat.property",
        flex: "33%",
        required: true,
      },
      {
        key: "uh_cat",
        flex: "33%",
        required: true,
      },
      {
        key: "uh_cat_ref",
        flex: "33%",
        required: true,
      },
      {
        key: "nb_pax",
        flex: "100%",
        // hidden: false,
      },
      {
        key: "separatorOccupation",
        flex: "100%",
      },
      {
        key: "date_range",
        flex: "100%",
      },
      {
        key: "weekdays",
        flex: "100%",
        required: true,
      },
      {
        key: "separatorFormula",
        flex: "100%",
      },
      {
        key: "user_input",
        flex: "100%",
        required: true,
      },
      {
        key: "rounding_value_v2",
        flex: "50%",
      },
      {
        key: "rounding_direction_v2",
        flex: "50%",
      },
      {
        key: "user_input_simulation",
        flex: "100%",
      },
    ],
    deletable: true,
  },
};

const SETUP_PRICE_PAGE_BUY_UP_GRID_TYPE_TAB: ConfigurationPageTables = {
  description: "CONF_PAGE_DESC_DEFAULT",
  type: ConfigurationPageType.TABLE,
  label: "BUY_UP_GRID_TYPES",
  path: "buy-ups-per-grid-type",
  model: {
    dynamicPath: true,
    entityName: "uh_cat.short_name",
    label: "BUY_UP_GRID_TYPES",
    path: "/api/v2/buy-ups-per-grid-type",
    name: "buy up grid type",
    attributes: [
      {
        key: "property.hebergement_nom_court",
        order: 1,
        label: "PROPERTY",
        visible: true,
      },
      {
        key: "uh_type_nandc.short_name",
        order: 2,
        label: "UHTYPE_NC",
        visible: true,
      },
      {
        key: "grid_type_source.code",
        order: 3,
        label: "GRID_TYPE_SOURCE",
        visible: true,
      },
      {
        key: "grid_type_target.code",
        order: 4,
        label: "GRID_TYPE_TARGET",
        visible: true,
      },
      {
        key: "uh_cats[].short_name",
        order: 4.5,
        label: "UH_CATS",
        type: FormatType.TEXT_FALLBACK_ALL_COUNT,
        numberOfColumnItemsToDisplay: 2,
        mustTranslateOptions: false,
        visible: true,
      },
      {
        align: "right",
        key: "start_date",
        order: 5,
        label: "DATE_START",
        type: FormatType.DATE,
        visible: true,
      },
      {
        align: "right",
        key: "end_date",
        order: 6,
        label: "DATE_END",
        type: FormatType.DATE,
        visible: true,
      },
      {
        key: "weekdays",
        order: 6.5,
        label: "DAYS_OF_OCCUPATION",
        type: FormatType.WEEKDAYS_ROUNDED,
        visible: true,
      },
      {
        key: "user_input",
        order: 6,
        label: "CALCUL_EDITOR",
        visible: true,
        type: FormatType.CALCUL_EDITOR,
        editorInputs: [
          {
            label: "NB_ADULT_PAX",
            value: "nb_pax",
            isVariable: true,
          },
          {
            label: "GRID_TYPE_SOURCE",
            value: "ref_gridtype",
            isVariable: true,
          },
        ],
      },
      {
        align: "right",
        key: "rounding_value_v2",
        order: 15,
        label: "ROUNDING_VALUE",
        visible: true,
      },
      {
        key: "rounding_direction_v2",
        order: 16,
        label: "ROUNDING_DIRECTION",
        type: FormatType.TRANSLATION,
        visible: true,
      },
      {
        key: "user_created_by.username",
        order: 17,
        label: "UPDATED_BY",
        visible: true,
      },
      {
        align: "right",
        key: "updated_at",
        order: 18,
        label: "UPDATED_AT",
        type: FormatType.DATE,
        visible: true,
      },
    ],
    filters: [
      {
        type: ConfigurationTableFilterType.Select,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        key: "property",
        labelKey: "hebergement_nom_court",
        label: "PROPERTY",
        lookup: "property",
      },
      {
        type: ConfigurationTableFilterType.Select,
        api: "/api/uh-types-nandc/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
        },
        key: "uh_type_nandc",
        labelKey: "short_name",
        label: "UHTYPE_NC",
        lookup: "uh_type_nandc",
      },
      {
        type: ConfigurationTableFilterType.Select,
        api: "/api/grid-types/",
        apiParams: {
          page_size: 100,
          ordering: "code",
        },
        key: "code",
        labelKey: "code",
        label: "GRID_TYPE_SOURCE",
        lookup: "grid_type_source",
      },
      {
        type: ConfigurationTableFilterType.Select,
        api: "/api/grid-types/",
        apiParams: {
          page_size: 100,
          ordering: "code",
        },
        key: "code",
        labelKey: "code",
        label: "GRID_TYPE_TARGET",
        lookup: "grid_type_target",
      },
      {
        type: ConfigurationTableFilterType.Search,
      },
      {
        type: ConfigurationTableFilterType.ResetButton,
      },
    ],
    export: { path: "/api/v2/buy-ups-per-grid-type/export" },
    import: { path: "/api/v2/buy-ups-per-grid-type/presign-url" },
  },
  options: {
    // Saves the rule and reprices its target grid from today on with it.
    extraSubmits: [
      { action: "apply_in_cascade", label: "BUY_UP_SAVE_AND_APPLY" },
    ],
    formFields: [
      {
        type: ConfigurationFormType.Select,
        api: "/api/properties/",
        key: "property",
        label: "PROPERTY",
        labelKey: "hebergement_nom_court",
        watchLookup: "property",
        watchedBy: "uh_cats",
      },
      {
        type: ConfigurationFormType.Select,
        api: "/api/uh-types-nandc/",
        key: "uh_type_nandc",
        label: "UHTYPE_NC",
        labelKey: "short_name",
      },
      {
        type: ConfigurationFormType.Select,
        api: "/api/grid-types/",
        apiParams: {
          page_size: 100,
          ordering: "code",
        },
        key: "grid_type_source",
        label: "GRID_TYPE_SOURCE",
        labelKey: "code",
      },
      {
        type: ConfigurationFormType.Select,
        api: "/api/grid-types/",
        apiParams: {
          page_size: 100,
          ordering: "code",
        },
        key: "grid_type_target",
        label: "GRID_TYPE_TARGET",
        labelKey: "code",
      },
      {
        type: ConfigurationFormType.SelectValues,
        api: "/api/uh-cats/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
        },
        key: "uh_cats",
        label: "UH_CATS",
        labelKey: "short_name",
        selectAllByDefault: true,
        watchField: [
          {
            paramsKey: "property",
            paramsValue: "id",
            watchFieldPath: "property",
          },
        ],
      },
      {
        key: "separatorOccupation",
        label: "BUY_UP_SECTION_OCCUPATION",
        subLabel: "BUY_UP_SECTION_OCCUPATION_DESC",
        type: ConfigurationFormType.Separator,
      },
      {
        key: "date_range",
        startDateField: "start_date",
        endDateField: "end_date",
        startDateLabel: "DATE_START",
        endDateLabel: "DATE_END",
        type: ConfigurationFormType.DateRangePicker,
      },
      {
        key: "weekdays",
        label: "DAYS_OF_OCCUPATION",
        type: ConfigurationFormType.WeekDaysRounded,
      },
      {
        key: "separatorFormula",
        label: "BUY_UP_SECTION_FORMULA",
        subLabel: "BUY_UP_SECTION_FORMULA_DESC",
        type: ConfigurationFormType.Separator,
      },
      {
        key: "user_input",
        type: ConfigurationFormType.CalculEditor,
        label: "CALCUL_EDITOR",
        tooltip: "CALCUL_EDITOR_TOOLTIP",
        inputMaxLength: 128,
        editorInputs: [
          {
            key: "plus",
            label: "+",
            value: "+",
          },
          {
            key: "minus",
            label: "-",
            value: "-",
          },
          {
            key: "multiplication",
            label: "*",
            value: "*",
          },
          {
            key: "division",
            label: "/",
            value: "/",
          },
          {
            key: "nb_pax",
            label: "NB_ADULT_PAX",
            translate: true,
            value: "nb_pax",
            isVariable: true,
          },
          {
            key: "grid",
            label: "GRID_TYPE_SOURCE",
            translate: true,
            value: "ref_gridtype",
            isVariable: true,
            required: true,
          },
        ],
      },
      {
        inputType: "number",
        key: "rounding_value_v2",
        label: "ROUNDING_VALUE",
        type: ConfigurationFormType.Input,
      },
      {
        key: "rounding_direction_v2",
        label: "ROUNDING_DIRECTION",
        type: ConfigurationFormType.StaticSelect,
      },
      {
        key: "user_input_simulation",
        type: ConfigurationFormType.CalculEditorSimulation,
        tooltip: "CALCUL_EDITOR_TOOLTIP_SIMULATION",
        label: "CALCUL_EDITOR_SIMULATION",
        api: "/api/v2/buy-ups-per-grid-type/apply/",
        watchField: "user_input",
        editorInputs: [
          {
            key: "nb_pax",
            label: "NB_ADULT_PAX",
            value: "nb_pax",
            isVariable: true,
          },
          {
            key: "ref_gridtype",
            label: "GRID_TYPE_SOURCE",
            value: "ref_gridtype",
            placeholder: "100",
            isVariable: true,
          },
        ],
      },
    ],
    creatable: true,
    createOptions: [
      {
        key: "property",
        flex: "50%",
        required: true,
        hasCheckAllOption: true,
      },
      {
        key: "uh_type_nandc",
        flex: "50%",
        required: true,
        hasCheckAllOption: true,
      },
      {
        key: "grid_type_source",
        flex: "50%",
        required: true,
      },
      {
        key: "grid_type_target",
        flex: "50%",
        required: true,
      },
      {
        key: "uh_cats",
        flex: "100%",
        required: true,
        hasCheckAllOption: true,
      },
      {
        key: "separatorOccupation",
        flex: "100%",
      },
      {
        key: "date_range",
        flex: "100%",
      },
      {
        key: "weekdays",
        flex: "100%",
        required: true,
      },
      {
        key: "separatorFormula",
        flex: "100%",
      },
      {
        key: "user_input",
        flex: "100%",
        required: true,
      },
      {
        key: "rounding_value_v2",
        flex: "50%",
      },
      {
        key: "rounding_direction_v2",
        flex: "50%",
      },
      {
        key: "user_input_simulation",
        flex: "100%",
      },
    ],
    copyable: false,
    displayFormError: true,
    editable: true,
    editOptions: [
      {
        key: "property",
        flex: "50%",
        required: true,
        hasCheckAllOption: true,
      },
      {
        key: "uh_type_nandc",
        flex: "50%",
        required: true,
        hasCheckAllOption: true,
      },
      {
        key: "grid_type_source",
        flex: "50%",
        required: true,
      },
      {
        key: "grid_type_target",
        flex: "50%",
        required: true,
      },
      {
        key: "uh_cats",
        flex: "100%",
        required: true,
      },
      {
        key: "separatorOccupation",
        flex: "100%",
      },
      {
        key: "date_range",
        flex: "100%",
      },
      {
        key: "weekdays",
        flex: "100%",
        required: true,
      },
      {
        key: "separatorFormula",
        flex: "100%",
      },
      {
        key: "user_input",
        flex: "100%",
        required: true,
      },
      {
        key: "rounding_value_v2",
        flex: "50%",
      },
      {
        key: "rounding_direction_v2",
        flex: "50%",
      },
      {
        key: "user_input_simulation",
        flex: "100%",
      },
    ],
    deletable: true,
  },
};

const SETUP_PRICE_PAGE_BUY_UP_CORE_MAPPING_TAB: ConfigurationPageTables = {
  description: "CORE_BUY_UP_DESCRIPTION",
  type: ConfigurationPageType.TABLE,
  label: "BUY_UP_CORE_MAPPING",
  path: "buy-ups-core-mapping",
  model: {
    dynamicPath: true,
    entityName: "uh_cat.short_name",
    label: "BUY_UP_CORE_MAPPING",
    path: "/api/buy-ups-core-mapping",
    name: "buy up core mapping",
    attributes: [
      {
        key: "source.description",
        order: 1,
        label: "SOURCE",
        visible: true,
      },
      {
        key: "target.description",
        order: 2,
        label: "TARGET",
        visible: true,
      },
      {
        align: "right",
        key: "start_date",
        order: 3,
        label: "DATE_START",
        type: FormatType.DATE,
        visible: true,
      },
      {
        align: "right",
        key: "end_date",
        order: 4,
        label: "DATE_END",
        type: FormatType.DATE,
        visible: true,
      },
      {
        key: "calculation_type_1",
        order: 5,
        label: "CALCULATION_TYPE",
        visible: true,
      },
      {
        key: "calculation_method_1",
        order: 6,
        label: "CALCULATION_METHOD",
        visible: true,
      },
      {
        align: "right",
        key: "value_1",
        order: 7,
        label: "VALUE",
        visible: true,
      },
      {
        align: "right",
        key: "rounding_value_1",
        order: 8,
        label: "ROUNDING_VALUE",
        visible: true,
      },
      {
        key: "rounding_direction_1",
        order: 9,
        label: "ROUNDING_DIRECTION",
        visible: true,
      },
      {
        key: "has_second_rule",
        order: 10,
        label: "HAS_SECOND_RULE",
        visible: true,
        type: FormatType.BOOLEAN,
        rules: [
          {
            type: "compare-with",
            value: false,
            operator: "eq",
            options: {
              color: "text.cell",
            },
          },
          {
            type: "compare-with",
            value: true,
            operator: "eq",
            options: {
              color: "text.accent",
            },
          },
        ],
      },
      {
        key: "apply_to_restrictions",
        order: 11,
        label: "APPLY_TO_RESTRICTIONS",
        visible: true,
        type: FormatType.BOOLEAN,
        rules: [
          {
            type: "compare-with",
            value: false,
            operator: "eq",
            options: {
              color: "text.cell",
            },
          },
          {
            type: "compare-with",
            value: true,
            operator: "eq",
            options: {
              color: "text.accent",
            },
          },
        ],
      },
      {
        key: "is_active",
        order: 12,
        label: "ACTIVE_F",
        visible: true,
        type: FormatType.BOOLEAN,
        rules: [
          {
            type: "compare-with",
            value: false,
            operator: "eq",
            options: {
              color: "text.cell",
            },
          },
          {
            type: "compare-with",
            value: true,
            operator: "eq",
            options: {
              color: "text.accent",
            },
          },
        ],
      },
    ],
    filters: [
      {
        type: ConfigurationTableFilterType.Select,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        key: "property",
        labelKey: "hebergement_nom_court",
        label: "SOURCE_PROPERTY",
        lookup: "source__uh_cat__property",
      },
      {
        type: ConfigurationTableFilterType.Select,
        api: "/api/uh-cats/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
        },
        key: "uh_cat",
        labelKey: "short_name",
        label: "SOURCE_UNIT",
        lookup: "source__uh_cat",
        watchLookup: "source__uh_cat__property",
      },
      {
        type: ConfigurationTableFilterType.Select,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        key: "property",
        labelKey: "hebergement_nom_court",
        label: "TARGET_PROPERTY",
        lookup: "target__uh_cat__property",
      },
      {
        type: ConfigurationTableFilterType.Select,
        api: "/api/uh-cats/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
        },
        key: "uh_cat",
        labelKey: "short_name",
        label: "TARGET_UNIT",
        lookup: "target__uh_cat",
        watchLookup: "target__uh_cat__property",
      },
      {
        type: ConfigurationTableFilterType.DatePicker,
        lookup: "start_date__gte",
        label: "START_DATE",
        maxDateField: "start_date__lte",
      },
      {
        type: ConfigurationTableFilterType.DatePicker,
        lookup: "end_date__lte",
        label: "END_DATE",
        minDateField: "end_date__gte",
      },
      {
        type: ConfigurationTableFilterType.Search,
      },
      {
        type: ConfigurationTableFilterType.ResetButton,
      },
    ],
    export: { path: "/api/buy-ups-core-mapping/export" },
    import: { path: "/api/buy-ups-core-mapping/import" },
  },
  options: {
    formFields: [
      {
        type: ConfigurationFormType.Text,
        key: "core_buy_up_description",
        label: "CORE_BUY_UP_DESCRIPTION",
        sx: {
          textAlign: "left",
          fontStyle: "normal",
        },
      },
      {
        type: ConfigurationFormType.Select,
        api: "/api/mapping/grid-types-mapping/",
        apiParams: {
          page_size: 20,
          ordering: "description",
          grid_type__isnull: false,
        },
        key: "source",
        label: "SOURCE",
        labelKey: "description",
        keepApiData: ["client_property_code", "client_uh_cat_code"],
      },
      {
        type: ConfigurationFormType.Select,
        api: "/api/mapping/grid-types-mapping/",
        apiParams: {
          page_size: 50,
          ordering: "description",
          client_property_code: {
            path: "source.client_property_code",
          },
          client_uh_cat_code: {
            path: "source.client_uh_cat_code",
          },
          grid_type__isnull: true,
        },
        key: "target",
        label: "TARGET",
        labelKey: "description",
        watchField: [
          {
            paramsKey: "client_property_code",
            paramsValue: "client_property_code",
            watchFieldPath: "source",
          },
          {
            paramsKey: "client_uh_cat_code",
            paramsValue: "client_uh_cat_code",
            watchFieldPath: "source",
          },
        ],
      },
      {
        key: "date_range",
        startDateField: "start_date",
        endDateField: "end_date",
        startDateLabel: "DATE_START",
        endDateLabel: "DATE_END",
        type: ConfigurationFormType.DateRangePicker,
        defaultValue: null,
      },
      {
        key: "rule_1",
        label: "RULE_1",
        type: ConfigurationFormType.Text,
        sx: {
          textAlign: "left",
          fontStyle: "normal",
        },
      },
      {
        key: "calculation_type_1",
        label: "CALCULATION_TYPE",
        type: ConfigurationFormType.StaticSelect,
      },
      {
        key: "calculation_method_1",
        label: "CALCULATION_METHOD",
        type: ConfigurationFormType.StaticSelect,
      },
      {
        inputType: "number",
        key: "value_1",
        label: "VALUE",
        type: ConfigurationFormType.Input,
      },
      {
        inputType: "number",
        key: "rounding_value_1",
        label: "ROUNDING_VALUE",
        type: ConfigurationFormType.Input,
      },
      {
        key: "rounding_direction_1",
        label: "ROUNDING_DIRECTION",
        type: ConfigurationFormType.StaticSelect,
      },
      {
        key: "toggle_1",
        label: "ADD_BUY_UP_RULE",
        toggleOnLabel: "RULE_2",
        toggleOnTooltip: "ADD_BUY_UP_RULE_TOOLTIP",
        type: ConfigurationFormType.ToggleSection,
        toggleConditions:
          "calculation_type_2,calculation_method_2,value_2,rounding_value_2,rounding_direction_2",
      },
      {
        key: "calculation_type_2",
        label: "CALCULATION_TYPE",
        type: ConfigurationFormType.StaticSelect,
        toggleParent: "toggle_1",
      },
      {
        key: "calculation_method_2",
        label: "CALCULATION_METHOD",
        type: ConfigurationFormType.StaticSelect,
        toggleParent: "toggle_1",
      },
      {
        inputType: "number",
        key: "value_2",
        label: "VALUE",
        type: ConfigurationFormType.Input,
        toggleParent: "toggle_1",
      },
      {
        inputType: "number",
        key: "rounding_value_2",
        label: "ROUNDING_VALUE",
        type: ConfigurationFormType.Input,
        toggleParent: "toggle_1",
      },
      {
        key: "rounding_direction_2",
        label: "ROUNDING_DIRECTION",
        type: ConfigurationFormType.StaticSelect,
        toggleParent: "toggle_1",
      },
      {
        key: "apply_to_restrictions",
        label: "APPLY_TO_RESTRICTIONS",
        type: ConfigurationFormType.Check,
        defaultValue: true,
      },
      {
        key: "is_active",
        label: "ACTIVE_F",
        type: ConfigurationFormType.Check,
        defaultValue: true,
      },
    ],
    creatable: true,
    createOptions: [
      {
        key: "core_buy_up_description",
        flex: "100%",
        readonly: true,
      },
      {
        key: "source",
        flex: "100%",
        required: true,
      },
      {
        key: "target",
        flex: "100%",
        required: true,
      },
      {
        key: "date_range",
        flex: "100%",
      },
      {
        key: "rule_1",
        flex: "100%",
      },
      {
        key: "calculation_type_1",
        flex: "50%",
        required: true,
      },
      {
        key: "calculation_method_1",
        flex: "50%",
        required: true,
      },
      {
        key: "value_1",
        flex: "33%",
        required: true,
      },
      {
        key: "rounding_value_1",
        flex: "33%",
        required: true,
      },
      {
        key: "rounding_direction_1",
        flex: "33%",
        required: true,
      },
      {
        key: "toggle_1",
        flex: "100%",
      },
      {
        key: "calculation_type_2",
        flex: "50%",
      },
      {
        key: "calculation_method_2",
        flex: "50%",
      },
      {
        key: "value_2",
        flex: "33%",
      },
      {
        key: "rounding_value_2",
        flex: "33%",
      },
      {
        key: "rounding_direction_2",
        flex: "33%",
      },
      {
        key: "apply_to_restrictions",
        flex: "50%",
      },
      {
        key: "is_active",
        flex: "50%",
      },
    ],
    copyable: false,
    editable: true,
    editOptions: [
      {
        key: "core_buy_up_description",
        flex: "100%",
        readonly: true,
      },
      {
        key: "source",
        flex: "100%",
        required: true,
      },
      {
        key: "target",
        flex: "100%",
        required: true,
      },
      {
        key: "grid_type_source",
        flex: "50%",
        required: true,
      },
      {
        key: "grid_type_target",
        flex: "50%",
        required: true,
      },
      {
        key: "date_range",
        flex: "100%",
      },
      {
        key: "rule_1",
        flex: "100%",
      },
      {
        key: "calculation_type_1",
        flex: "50%",
        required: true,
      },
      {
        key: "calculation_method_1",
        flex: "50%",
        required: true,
      },
      {
        key: "value_1",
        flex: "33%",
        required: true,
      },
      {
        key: "rounding_value_1",
        flex: "33%",
        required: true,
      },
      {
        key: "rounding_direction_1",
        flex: "33%",
        required: true,
      },
      {
        key: "toggle_1",
        flex: "100%",
      },
      {
        key: "calculation_type_2",
        flex: "50%",
      },
      {
        key: "calculation_method_2",
        flex: "50%",
      },
      {
        key: "value_2",
        flex: "33%",
      },
      {
        key: "rounding_value_2",
        flex: "33%",
      },
      {
        key: "rounding_direction_2",
        flex: "33%",
      },
      {
        key: "apply_to_restrictions",
        flex: "50%",
      },
      {
        key: "is_active",
        flex: "50%",
      },
    ],
    deletable: true,
  },
};

const SETUP_PRICE_PAGE_PRICE_BOUNDS_TAB: ConfigurationPageTables = {
  description: "CONF_PAGE_DESC_DEFAULT",
  type: ConfigurationPageType.TABLE,
  label: "PRICE_BOUNDS_UNIT",
  path: "price-bounds-rules",
  model: {
    dynamicPath: true,
    entityName: "id",
    label: "PRICE_BOUNDS",
    path: "/api/price-bounds-rules",
    name: "price bounds rule",
    attributes: [
      {
        key: "property.hebergement_nom_court",
        order: 1,
        label: "PROPERTY",
        visible: true,
      },
      {
        key: "uh_type_nandc.short_name",
        order: 2,
        label: "UHTYPE_NC",
        visible: true,
      },
      {
        key: "uh_cat.short_name",
        order: 3,
        label: "UH_CAT",
        visible: true,
      },
      {
        align: "right",
        key: "date_from",
        order: 4,
        label: "DATE_START",
        type: FormatType.DATE,
        visible: true,
      },
      {
        align: "right",
        key: "date_to",
        order: 5,
        label: "DATE_END",
        type: FormatType.DATE,
        visible: true,
      },
      {
        align: "right",
        key: "floor",
        order: 6,
        label: "PRICE_BOUNDS_FLOOR",
        visible: true,
      },
      {
        align: "right",
        key: "ceiling",
        order: 7,
        label: "PRICE_BOUNDS_CEILING",
        visible: true,
      },
      {
        align: "right",
        key: "floor_tier_level",
        order: 8,
        label: "PRICE_BOUNDS_FLOOR_TIER_LEVEL",
        visible: true,
      },
      {
        align: "right",
        key: "ceiling_tier_level",
        order: 9,
        label: "PRICE_BOUNDS_CEILING_TIER_LEVEL",
        visible: true,
      },
      {
        align: "right",
        key: "updated_at",
        order: 10,
        label: "UPDATED_AT",
        type: FormatType.DATE,
        visible: true,
      },
    ],
    filters: [
      {
        type: ConfigurationTableFilterType.Select,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        key: "property",
        labelKey: "hebergement_nom_court",
        label: "PROPERTY",
        lookup: "property",
      },
      {
        type: ConfigurationTableFilterType.Search,
      },
      {
        type: ConfigurationTableFilterType.ResetButton,
      },
    ],
    actions: [
      {
        type: "link",
        // `useFiltersUrl` decodes `?status=...` into a `FilterType.STATUS`
        // filter on page mount, so the workflow opens already narrowed
        // to OUT_OF_RANGE rows.
        href: "/price/?status=out_of_range",
        label: "PRICE_BOUNDS_VIEW_BLOCKED_PRICES",
      },
    ],
  },
  options: {
    formFields: [
      {
        type: ConfigurationFormType.Select,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        key: "property",
        label: "PROPERTY",
        labelKey: "hebergement_nom_court",
        watchedBy: ["uh_type_nandc", "uh_cat"],
      },
      // uh_type_nandc and uh_cat are mutually exclusive. The `hide` action
      // (implemented in FormBuilder.tsx) hides each one when the other
      // is filled — backed by the DB's `price_bounds_exactly_one_scope`
      // CheckConstraint.
      // `uh-types-nandc`, not `uh-types`: the rule stores a UhTypeNandc id —
      // that is what `_matching_rules` compares against a category's
      // `uh_type_nandc_id`. The other endpoint serves a different table whose
      // ids merely look alike, so a bound scoped from it guarded the wrong
      // rooms without ever failing.
      {
        type: ConfigurationFormType.Select,
        api: "/api/uh-types-nandc/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
          property: { path: "property.id" },
        },
        key: "uh_type_nandc",
        label: "UHTYPE_NC",
        labelKey: "short_name",
        watchField: [
          {
            paramsKey: "property",
            paramsValue: "id",
            watchFieldPath: "property",
          },
        ],
        relatedInputAction: { action: "hide", relatedInput: "uh_cat" },
      },
      {
        type: ConfigurationFormType.Select,
        api: "/api/uh-cats/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
          property: { path: "property.id" },
        },
        key: "uh_cat",
        label: "UH_CAT",
        labelKey: "short_name",
        watchField: [
          {
            paramsKey: "property",
            paramsValue: "id",
            watchFieldPath: "property",
          },
        ],
        relatedInputAction: { action: "hide", relatedInput: "uh_type_nandc" },
      },
      {
        key: "date_range",
        startDateField: "date_from",
        endDateField: "date_to",
        startDateLabel: "DATE_START",
        endDateLabel: "DATE_END",
        type: ConfigurationFormType.DateRangePicker,
        defaultValue: null,
      },
      {
        inputType: "number",
        key: "floor",
        label: "PRICE_BOUNDS_FLOOR",
        type: ConfigurationFormType.Input,
        // Backend constraint: at least one of floor / ceiling must be set.
        // Form-level cross-field validation is in the serializer.
        helperRender: () =>
          createElement(SuggestedBoundHint, { side: "floor" }),
      },
      {
        inputType: "number",
        key: "ceiling",
        label: "PRICE_BOUNDS_CEILING",
        type: ConfigurationFormType.Input,
        helperRender: () =>
          createElement(SuggestedBoundHint, { side: "ceiling" }),
      },
      // A bound is an amount OR a pricing-tier level, never both on the same
      // side: two forms of one bound would need an arbitrary tie-break every
      // time they disagree. The backend rejects it; the form keeps the pairs
      // visually apart under their own separator.
      {
        key: "separatorTierBounds",
        label: "PRICE_BOUNDS_SECTION_TIER_BOUNDS",
        subLabel: "PRICE_BOUNDS_SECTION_TIER_BOUNDS_DESC",
        type: ConfigurationFormType.Separator,
      },
      {
        inputType: "number",
        key: "floor_tier_level",
        label: "PRICE_BOUNDS_FLOOR_TIER_LEVEL",
        type: ConfigurationFormType.Input,
      },
      {
        inputType: "number",
        key: "ceiling_tier_level",
        label: "PRICE_BOUNDS_CEILING_TIER_LEVEL",
        type: ConfigurationFormType.Input,
      },
      {
        key: "separatorScope",
        label: "PRICE_BOUNDS_SECTION_SCOPE",
        subLabel: "PRICE_BOUNDS_SECTION_SCOPE_DESC",
        type: ConfigurationFormType.Separator,
      },
      {
        key: "separatorBounds",
        label: "PRICE_BOUNDS_SECTION_BOUNDS",
        subLabel: "PRICE_BOUNDS_SECTION_BOUNDS_DESC",
        type: ConfigurationFormType.Separator,
      },
    ],
    creatable: true,
    createOptions: [
      // All three are optional. A rule with no property is a global
      // default; with property but no uh_type_nandc/uh_cat it applies to
      // the whole property. The `hide` action keeps uh_type_nandc and uh_cat
      // mutually exclusive when one IS set.
      { key: "separatorScope", flex: "100%" },
      { key: "property", flex: "100%" },
      { key: "uh_type_nandc", flex: "50%" },
      { key: "uh_cat", flex: "50%" },
      { key: "date_range", flex: "100%" },
      { key: "separatorBounds", flex: "100%" },
      { key: "floor", flex: "50%" },
      { key: "ceiling", flex: "50%" },
      { key: "separatorTierBounds", flex: "100%" },
      { key: "floor_tier_level", flex: "50%" },
      { key: "ceiling_tier_level", flex: "50%" },
    ],
    editable: true,
    editOptions: [
      { key: "separatorScope", flex: "100%" },
      { key: "property", flex: "100%" },
      { key: "uh_type_nandc", flex: "50%" },
      { key: "uh_cat", flex: "50%" },
      { key: "date_range", flex: "100%" },
      { key: "separatorBounds", flex: "100%" },
      { key: "floor", flex: "50%" },
      { key: "ceiling", flex: "50%" },
      { key: "separatorTierBounds", flex: "100%" },
      { key: "floor_tier_level", flex: "50%" },
      { key: "ceiling_tier_level", flex: "50%" },
    ],
    copyable: false,
    // Fully-null rule (no scope) is the tenant's global default —
    // backend refuses delete in `perform_destroy`. Show the trash icon
    // disabled with a tooltip so the Action column stays aligned and
    // operators understand why it's blocked.
    deletable: (row: any) =>
      row?.property || row?.uh_type_nandc || row?.uh_cat
        ? true
        : { disabled: true, reason: "PRICE_BOUNDS_GLOBAL_DELETE_FORBIDDEN" },
  },
};

export const SETUP_PRICE_PAGE = {
  type: ConfigurationPageType.TABS,
  count: 0,
  label: "PRICES",
  pageLabel: "PRICE_TITLE_SETUP",
  menuLabel: "PRICES",
  name: "Price",
  path: "price",
  tabs: [
    SETUP_PRICE_PAGE_PRICES_TAB,
    SETUP_PRICE_PAGE_PRICE_BOUNDS_TAB,
    SETUP_PRICE_PAGE_BUY_UP_TAB,
    SETUP_PRICE_PAGE_BUY_UP_GRID_TYPE_TAB,
    SETUP_PRICE_PAGE_BUY_UP_CORE_MAPPING_TAB,
  ],
};
